import {
  Button,
  ButtonSize,
  ButtonVariant,
  Hide,
  Show
} from "shared/components";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { instance } from "api/apiBase";
import { QUERY_SETTINGS } from "api/landlord/landlord";
import { getErrorMessage } from "utils/error";

import { Clock, Settings04 } from "untitledui-js-base";

import {
  Applicant,
  Application,
  MakeDecisionRequirements
} from "types/application";
import { ApplicationStatusBanner } from "../ApplicationStatusBanner";
import { ApplicationStatus, Invitee } from "shared/types";
import { BreadCrumbs } from "shared/components/BreadCrumbs";
import { cx, formatPhone } from "shared/utils";
import { ContextMenuPopover } from "shared/components/ContextMenuPopover";
import { useAuthContext } from "providers/Auth";
import { toast } from "react-toastify";
import { getFullName } from "shared/utils/get-full-name";
import { usePopoverActions } from "views/applications-id/components/LayoutHeader/hooks/usePopoverActions";
import { UndoDecisionModal } from "./components/UndoDecisionModal";

import { ApplicationActions } from "shared/utils/application-actions";
import { resumeApplication } from "shared/app/OnHoldResume/api";
import {
  ApplicationLike,
  getApplicationUIStatus
} from "utils/get-application-ui-status";
import { FilterIcon } from "@hugeicons-pro/core-stroke-rounded";
import { useNavigate } from "react-router-dom";
import { HeaderTabs } from "views/applications-id/components/LayoutHeader/types";
import { Tabs } from "shared/components/Tabs";
import { AssigneeSelect } from "shared/app/ApplicationsTableView/components/AssigneeSelect";
import { HugeiconsIcon } from "@hugeicons/react";
import { Note02Icon } from "@hugeicons-pro/core-stroke-rounded";

interface ILayoutHeader {
  applicationId: string;
  applicants?: Applicant[];
  application: Application;
  invitees: Invitee[];
  disableDecisionFlow?: boolean;
  applicationStatus: ApplicationStatus;
  decisionAt: string | null;
  submittedAt: string | null;
  setShowActivityLog: (value: boolean) => void;
  setShowDecisionFlowModal?: (isOpen: boolean) => void;
  setDecisionConfirmationModal?: (isOpen: boolean) => void;
  setShowInviteApplicantsModal?: (isOpen: boolean) => void;
  setShowNotesModal?: (isOpen: boolean) => void;
  viewTemplate: () => void;
  onCancelClick?: () => void;
  onUncancelClick?: () => void;
  onMoveClick?: () => void;
  onSplitClick?: () => void;
  onRefundClick?: () => void;
  requirements?: MakeDecisionRequirements[];
  hideApplicantNamesAndUnitInfo?: boolean;
  selectedTab: HeaderTabs;
  onTabChange: (tab: HeaderTabs) => void;
  numberOfNotes: number;
}

export const LayoutHeader = ({
  applicationId,
  applicants,
  application,
  applicationStatus,
  decisionAt,
  viewTemplate,
  setShowActivityLog,
  setShowDecisionFlowModal,
  setDecisionConfirmationModal,
  setShowInviteApplicantsModal,
  setShowNotesModal,
  onCancelClick,
  onUncancelClick,
  onSplitClick,
  onMoveClick,
  onRefundClick,
  requirements,
  hideApplicantNamesAndUnitInfo,
  selectedTab,
  onTabChange,
  numberOfNotes
}: ILayoutHeader) => {
  const { permissions, type } = useAuthContext();
  const navigate = useNavigate();

  const { data: undoDecisionDays } = useQuery({
    queryKey: [QUERY_SETTINGS],
    queryFn: () => instance.get("/portal/v1/settings"),
    select: (response) => {
      const applicationSettings = response.data.settings?.find(
        (setting: { name: string }) => setting.name === "application_settings"
      );
      const undoDecision = applicationSettings?.value?.undo_decision;
      if (!undoDecision || undoDecision.active === false) return null;
      return undoDecision.days ?? 30;
    }
  });

  const applicationForStatus: ApplicationLike = {
    status: application.status,
    applicants: application.applicants
  };

  const appActions = new ApplicationActions({
    status: applicationStatus,
    permissions,
    applicants,
    application,
    decisionAt,
    requirements,
    undoDecisionDays: undoDecisionDays !== undefined ? undoDecisionDays : 30
  });

  const tabs = useMemo(() => {
    const ret = [HeaderTabs.APPLICATION];

    if (type.isBoomCRM) ret.push(HeaderTabs.TASKS);

    return ret;
  }, [type]);

  const displayStatus = getApplicationUIStatus(applicationForStatus);
  const actions = usePopoverActions({
    applicationId,
    actions: appActions,
    onMoveClick,
    onRefundClick,
    onCancelClick,
    onUncancelClick,
    onSplitClick
  });

  const [undoDecisionModalOpened, setUndoDecisionModalOpened] = useState(false);
  const [notifyApplicant, setNotifyApplicant] = useState(false);
  const queryClient = useQueryClient();
  const { mutate: undoDecision, isPending: undoDecisionPending } = useMutation({
    mutationFn: () => {
      return instance.post(
        `portal/v1/applications/${applicationId}/undo_decision`,
        {
          notify_applicant: notifyApplicant
        }
      );
    },
    onSuccess: () => {
      toast.success("Application successfully updated");
      queryClient.invalidateQueries({
        queryKey: ["APPLICATION", applicationId]
      });
      queryClient.invalidateQueries({
        queryKey: ["APPLICANTS_TABLE"]
      });
      setUndoDecisionModalOpened(false);
    },
    onError: (e) => {
      toast.error(getErrorMessage(e));
    }
  });

  const {
    hidden: isMakeDecisionHidden,
    disabled: isMakeDecisionDisabled,
    tooltip: cannotMakeDecisionText
  } = appActions.makeDecision;
  const canViewLead =
    type.isBoomCRM &&
    Boolean(application.lead_id) &&
    Number(application.total_applicants) <= 1;

  return (
    <div>
      <div className="flex items-center justify-between border-b border-alpha-40 px-3 lg:px-6 py-3 lg:flex-nowrap flex-wrap">
        <div className="flex items-center gap-2.5 lg:h-auto h-5">
          <BreadCrumbs
            crumbs={[
              {
                title: "Applications",
                href: "/applications"
              },
              {
                title:
                  applicants
                    ?.map((app) => {
                      if (app.first_name && app.last_name)
                        return getFullName({
                          firstName: app.first_name,
                          lastName: app.last_name,
                          middleName: app.middle_name,
                          short: true
                        });
                      return app.email ?? formatPhone(app.phone);
                    })
                    .join(" • ") || "Application with invitees",
                href: `/applications/${applicationId}`
              }
            ]}
          />
        </div>
        <div className="flex">
          <Show if={canViewLead}>
            <Button
              borderLess
              variant={ButtonVariant.BASE}
              size={ButtonSize.SM}
              HugeIcon={FilterIcon}
              className="hidden lg:flex !text-content-400 !stroke-content-400"
              onClick={() => {
                if (application.lead_id)
                  navigate(`/leads/${application.lead_id}`);
              }}
            >
              View lead
            </Button>
          </Show>
          <Button
            borderLess
            variant={ButtonVariant.BASE}
            size={ButtonSize.SM}
            Icon={Settings04}
            className="hidden lg:flex !text-content-400 !stroke-content-400"
            onClick={() => {
              viewTemplate();
            }}
          >
            Screening settings
          </Button>
          <Button
            borderLess
            variant={ButtonVariant.BASE}
            size={ButtonSize.SM}
            className="hidden lg:flex !text-content-400 !stroke-content-400"
            Icon={Clock}
            onClick={() => setShowActivityLog(true)}
          >
            Activity log
          </Button>
        </div>
      </div>
      <ApplicationStatusBanner status={displayStatus} />

      <div
        className={cx(
          "flex items-center justify-between p-3 lg:p-6 flex-wrap lg:flex-nowrap transition-all",
          hideApplicantNamesAndUnitInfo ? "h-[72px]" : "h-[88px]"
        )}
      >
        <Hide if={hideApplicantNamesAndUnitInfo}>
          <h4 className="text-xl font-bold text-content-1000 flex flex-wrap items-center gap-4 animate-bounce-in">
            {applicants?.map((app, index) => (
              <span
                key={app.id || index}
                className="flex items-center border-b border-transparent gap-4"
              >
                {getFullName({
                  firstName: app.first_name,
                  lastName: app.last_name,
                  middleName: app.middle_name,
                  short: true
                })}
                <Show if={index !== applicants.length - 1}>
                  <span className="size-1.5 rounded-full bg-alpha-100" />
                </Show>
              </span>
            ))}
          </h4>
        </Hide>
        <div className="flex items-center justify-end gap-2 ml-auto">
          <div className="flex items-center gap-2 mr-[24px]">
            <span className="text-sm font-medium text-content-300">
              Assigned to
            </span>
            <AssigneeSelect
              value={application.assignees?.[0]?.id ?? null}
              rowId={String(application.id)}
              inputClassName="w-[185px]"
            />
          </div>
          <ContextMenuPopover borderLess={false} actions={actions} />

          <Button
            variant={ButtonVariant.BASE}
            size={ButtonSize.SM}
            onClick={() => setShowNotesModal?.(true)}
          >
            <div className="flex items-center gap-2">
              <Show if={Boolean(numberOfNotes)}>
                <span className="relative inline-flex shrink-0">
                  <HugeiconsIcon
                    icon={Note02Icon}
                    size={20}
                    strokeWidth={1.5}
                  />
                  <span className="absolute -top-1 -right-1.5 flex h-[17.5px] min-w-[17.5px] items-center justify-center rounded-sm bg-primary-500 text-[10px] font-medium leading-4 text-white border-[1.5px] border-white">
                    {numberOfNotes}
                  </span>
                </span>
              </Show>
              <span>Notes</span>
            </div>
          </Button>

          {!appActions.undoDecision.hidden ? (
            <Button
              variant={ButtonVariant.BASE}
              size={ButtonSize.SM}
              onClick={() => setUndoDecisionModalOpened(true)}
              disabled={appActions.undoDecision.disabled}
              tooltip={appActions.undoDecision.tooltip}
              tooltipZIndex={21}
            >
              Undo decision
            </Button>
          ) : (
            <Button
              variant={ButtonVariant.BASE}
              size={ButtonSize.SM}
              onClick={() => setShowInviteApplicantsModal?.(true)}
              disabled={appActions.inviteApplicant.disabled}
              className="hidden md:flex"
              tooltip={appActions.inviteApplicant.tooltip}
              tooltipZIndex={21}
            >
              Invite applicant
            </Button>
          )}
          <Show if={!isMakeDecisionHidden}>
            <Button
              variant={ButtonVariant.PRIMARY}
              size={ButtonSize.SM}
              onClick={() => {
                applicationStatus === ApplicationStatus.Submitted
                  ? setShowDecisionFlowModal?.(true)
                  : setDecisionConfirmationModal?.(true);
              }}
              className="hidden md:flex"
              disabled={isMakeDecisionDisabled}
              tooltip={cannotMakeDecisionText}
              tooltipPlacement="bottom-end"
              tooltipZIndex={2}
            >
              Make decision
            </Button>
          </Show>

          <Show if={applicationStatus === ApplicationStatus.OnHold}>
            <Button
              variant={ButtonVariant.PRIMARY}
              size={ButtonSize.SM}
              onClick={() => {
                resumeApplication({
                  applicationId,
                  onSuccess: () => {
                    queryClient.refetchQueries({
                      queryKey: ["APPLICATION", applicationId]
                    });
                  }
                });
              }}
              className="hidden md:flex"
            >
              Resume
            </Button>
          </Show>
        </div>
      </div>

      {undoDecisionModalOpened ? (
        <UndoDecisionModal
          opened={undoDecisionModalOpened}
          onClose={() => {
            setUndoDecisionModalOpened(false);
          }}
          undoDecisionPending={undoDecisionPending}
          onUndo={() => undoDecision()}
          setNotifyApplicant={setNotifyApplicant}
          notifyApplicant={notifyApplicant}
        />
      ) : null}

      <Show if={tabs.length > 1}>
        <div className="px-6 border-t border-alpha-40 pt-4">
          <Tabs
            tabs={tabs}
            selected={selectedTab}
            setActiveTab={(t) => onTabChange(t as HeaderTabs)}
            displayMap={{
              [HeaderTabs.APPLICATION]: "Application",
              [HeaderTabs.TASKS]: "Tasks"
            }}
            variant="underline"
          />
        </div>
      </Show>
    </div>
  );
};
